import { z, ZodTypeAny } from "zod";
import { LatLng } from "./primitives";
import polylineParser from "@mapbox/polyline";

type ValhallaLatLng = {
	lat: number;
	lon: number;
};

export function toValhalla(coord: number[]): { lat: number; lon: number } {
	return {
		lat: coord[0],
		lon: coord[1],
	};
}

export function decode(str: string): LatLng[] {
	return polylineParser.decode(str, 6);
}

export type LocateRequest = {
	locations: ValhallaLatLng[];
	costing: "bicycle";
	verbose?: boolean;
};

export type BicyleCostingOptions = {
	use_hills?: number;
	use_roads?: number;
	use_oneways?: number;
	use_bikepaths?: number;
};

export type DirectionsRequest = {
	locations: {
		lat: number;
		lon: number;
	}[];
	costing: "bicycle" | "auto";
	costing_options?: {
		bicycle?: BicyleCostingOptions;
	};
	directions_options?: {
		units?: "miles";
	};
	exclude_locations?: {
		lat: number;
		lon: number;
	}[];
};

export async function valhalla<Req, Res extends ZodTypeAny>(
	path: string,
	req: Req,
	res: Res
): Promise<z.infer<Res>> {
	const data = await fetch("https://valhalla.bigboy.us/" + path, {
		method: "POST",
		body: JSON.stringify(req),
	}).then((r) => r.json());
	try {
		return res.parse(data);
	} catch (e) {
		console.error(e);
		return Promise.reject(e);
	}
}

const Route = z.object({
	trip: z.object({
		legs: z.array(
			z.object({
				maneuvers: z.array(
					z.object({
						street_names: z.array(z.string()).optional(),
					})
				),
				shape: z.string(),
			})
		),
		summary: z.object({
			time: z.number(),
		}),
	}),
});

// avoidsSelector(store.getState()).map((a) =>
// 				toValhalla(midpoint(decode(a.polyline)))
// 			)

export async function fetchDirections(
	fromto: [LatLng, LatLng],
	exclude_locations?: ValhallaLatLng[],
	costing_options?: BicyleCostingOptions
): Promise<{
	from: LatLng;
	to: LatLng;
	coords: LatLng[];
	time: number;
}> {
	const parsed = await valhalla<DirectionsRequest, typeof Route>(
		"route",
		{
			locations: fromto.map(toValhalla),
			costing: "bicycle",
			costing_options: {
				bicycle: {
					...costing_options,
					use_hills: 1,
				},
			},
			directions_options: {
				units: "miles",
			},
			exclude_locations,
		},
		Route
	);
	let coords: LatLng[] = [];
	const already: Record<string, boolean> = {};
	parsed.trip.legs.forEach((leg) => {
		// some responses double up the same shape, for
		// reasons i don't fully understand
		if (!already[leg.shape]) {
			coords = coords.concat(decode(leg.shape));
			already[leg.shape] = true;
		}
	});
	return {
		from: fromto[0],
		to: fromto[1],
		coords,
		time: parsed.trip.summary.time,
	};
}

const Match = z.array(
	z.object({
		edges: z.array(
			z.object({
				edge_info: z.object({
					shape: z.string(),
				}),
				edge_id: z.object({
					id: z.number(),
				}),
			})
		),
	})
);

export async function edgeAtLocation(
	latlng: LatLng
): Promise<{ polyline: string; id: number }[]> {
	const parsed = await valhalla<LocateRequest, typeof Match>(
		"locate",
		{
			verbose: true,
			locations: [[latlng[0], latlng[1]]].map(toValhalla),
			costing: "bicycle",
		},
		Match
	);
	return parsed.reduce((acc, thing) => {
		return acc.concat(
			thing.edges.map((edge) => ({
				polyline: edge.edge_info.shape,
				id: edge.edge_id.id,
			}))
		);
	}, [] as { polyline: string; id: number }[]);
}
